import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { AccordionGroupNodeView } from './accordion-group-node-view'
import { nanoid } from 'nanoid'

export interface AccordionGroupAttributes {
  // AccordionGroup typically doesn't have custom attributes
  // but we can add some if needed
  id?: string
}

export const AccordionGroupSpec = {
  group: 'block',
  content: 'Accordion+',
  attrs: { id: { default: nanoid(10) } },
  toDOM() { return ['div', { 'data-accordion-group': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-accordion-group]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    accordionGroup: {
      /**
       * Insert an accordion group
       */
      insertAccordionGroup: (
        attributes?: AccordionGroupAttributes
      ) => ReturnType
      /**
       * Update accordion group attributes
       */
      updateAccordionGroup: (
        attributes: Partial<AccordionGroupAttributes>
      ) => ReturnType
    }
  }
}

export const AccordionGroupExtension = Node.create({
  name: 'AccordionGroup',

  group: 'block',

  content: 'Accordion+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="AccordionGroup"]',
      },
      {
        tag: 'div.accordion-group-container',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'AccordionGroup',
        class: 'group/accordiongroup relative my-5 p-0.5',
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(AccordionGroupNodeView)
  },

  addCommands() {
    return {
      insertAccordionGroup:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
            content: [
              {
                type: 'Accordion',
                attrs: {
                  title: 'Accordion Title',
                  defaultOpen: true,
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              },
              {
                type: 'Accordion',
                attrs: {
                  title: 'Accordion Title',
                  defaultOpen: true,
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              }
            ],
          })
        },
      updateAccordionGroup:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the accordion group with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of an accordion group
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})
